import React from "react";
import {
  Box, Chip, CircularProgress, IconButton, Stack, Switch, Typography,
} from "@mui/material";
import RuleIcon from "@mui/icons-material/Rule";
import CelebrationIcon from "@mui/icons-material/Celebration";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  useGetPlaylistGroupQuery, useUpdatePlaylistGroupItemMutation, useGetHolidaySuggestionsQuery,
} from "../../../Api/attoDeskApi";
import {
  IPlaylistGroup, IPlaylistGroupItem, IHolidaySuggestion, MenuType,
} from "../../../Api/Interface/api.interface";
import { TabCard, NeedsGroup } from "./menuTabsUi";
import { useNotifier } from "../../../Core/Notifier";
import { BORDER, NAVY, TEXT_SECONDARY } from "../../../theme/tokens";
import { MENU_TYPE_LABELS } from "./menuScheduleShared";
import { MenuTabProps } from "./MenuTabs";

// Mirrors the backend MENU_TYPE_RANK for display ordering.
const RANK: Record<string, number> = {
  emergency_override: 90, promotional_campaign: 60, holiday_menu: 50,
  daily_special: 40, time_based_menu: 30, pos_synced_menu: 25,
  standard_playlist: 20, default_menu: 0,
};

const PRIORITY_ORDER = [
  "Emergency / manual override", "Dated promotional campaign", "Holiday menu",
  "Day-specific special", "Time-based menu", "Default menu",
];

const MenuRulesTab: React.FC<MenuTabProps> = ({ groupId, companyId }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, isLoading, refetch } = useGetPlaylistGroupQuery(groupId ?? 0, { skip: !groupId });
  const { data: holidayData } = useGetHolidaySuggestionsQuery(
    { year: new Date().getFullYear(), province: "ON" }
  );
  const [update] = useUpdatePlaylistGroupItemMutation();

  if (!groupId) return <NeedsGroup what="Menu rules" />;

  const group = data?.data as unknown as IPlaylistGroup | undefined;
  const items: IPlaylistGroupItem[] = [...(group?.items ?? [])].sort((a, b) => {
    const pa = a.priority ?? 0, pb = b.priority ?? 0;
    if (pb !== pa) return pb - pa;
    return (RANK[b.playlist?.menu_type || "standard_playlist"] ?? 0)
         - (RANK[a.playlist?.menu_type || "standard_playlist"] ?? 0);
  });

  const holidays = ((holidayData?.data as IHolidaySuggestion[]) ?? [])
    .filter((h) => new Date(h.date) >= new Date(new Date().toDateString()))
    .slice(0, 6);

  const bump = async (it: IPlaylistGroupItem, delta: number) => {
    try {
      await update({ groupId, itemId: it.id, body: { priority: Math.max(0, (it.priority ?? 0) + delta), force: true } }).unwrap();
      refetch();
    } catch { showErrorMessage("Could not update priority."); }
  };
  const toggleActive = async (it: IPlaylistGroupItem, on: boolean) => {
    try {
      await update({ groupId, itemId: it.id, body: { is_active: on, force: true } }).unwrap();
      showMessage(on ? "Rule enabled" : "Rule disabled");
      refetch();
    } catch { showErrorMessage("Could not update the rule."); }
  };

  return (
    <Stack spacing={2.5}>
      <TabCard title="Rule Priority" subtitle="Highest priority menu wins when schedules overlap"
        icon={<RuleIcon fontSize="small" />}>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
          {PRIORITY_ORDER.map((label, i) => (
            <React.Fragment key={label}>
              <Chip size="small" label={`${i + 1}. ${label}`}
                sx={{ fontWeight: 700, bgcolor: i === 0 ? NAVY : "#EEF2F6", color: i === 0 ? "#fff" : NAVY }} />
            </React.Fragment>
          ))}
        </Box>

        {isLoading ? (
          <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>
        ) : items.length === 0 ? (
          <Typography sx={{ color: TEXT_SECONDARY, textAlign: "center", py: 3 }}>
            No menus in this group yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {items.map((it) => (
              <Box key={it.id} sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                border: `1px solid ${BORDER}`, borderRadius: 2, px: 1.5, py: 1, gap: 1,
                opacity: it.is_active ? 1 : 0.55,
              }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 700, color: NAVY }}>{it.playlist?.name}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY }}>
                    {MENU_TYPE_LABELS[(it.playlist?.menu_type as MenuType) || "standard_playlist"]} · priority {it.priority ?? 0}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <IconButton size="small" onClick={() => bump(it, +1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => bump(it, -1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                  <Switch checked={it.is_active} onChange={(e) => toggleActive(it, e.target.checked)} />
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </TabCard>

      <TabCard title="Holiday & Event Ideas" subtitle="Upcoming dates you might build a menu for"
        icon={<CelebrationIcon fontSize="small" />}>
        {holidays.length === 0 ? (
          <Typography sx={{ color: TEXT_SECONDARY, textAlign: "center", py: 2 }}>
            No upcoming suggestions.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {holidays.map((h) => (
              <Box key={h.key} sx={{
                border: `1px solid ${BORDER}`, borderRadius: 2, px: 1.5, py: 1, minWidth: 180, flex: "1 1 200px",
              }}>
                <Typography sx={{ fontWeight: 800, color: NAVY, fontSize: 14 }}>{h.name}</Typography>
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 0.5 }}>
                  {new Date(h.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                </Typography>
                <Chip label="Create a holiday menu" size="small"
                  sx={{ fontSize: 10.5, fontWeight: 700, bgcolor: "rgba(212,175,55,0.18)", color: "#8A6D1A" }} />
              </Box>
            ))}
          </Box>
        )}
        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 1.5 }}>
          Create a playlist with menu type <b>Holiday Menu</b> and set its date range to schedule one.
        </Typography>
      </TabCard>
    </Stack>
  );
};

export default MenuRulesTab;
